import "bootstrap/dist/css/bootstrap.css";
import { Suspense, useEffect, useRef, useState } from "react";
import "typeface-montserrat";
import "./assets/css/alert.scss";
import "./assets/css/calender-custom.scss";
import "./assets/css/chip.scss";
import "./assets/css/common.scss";
import "./assets/css/custom.scss";
import "./assets/css/dropdown.scss";
import "./assets/css/internal.scss";
import "./assets/css/modal.scss";
import "./assets/css/navbar.scss";
import "./assets/css/tooltip.scss";
// Last of the global sheets: it re-points the tokens the others were built on
// for the dark theme. Specificity, not order, is what makes it win — but
// keeping it last matches how it reads.
import "./assets/css/theme.scss";

import "react-calendar/dist/Calendar.css";
import { Provider, useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Cookies from "universal-cookie";
import AccessDenied from "./app/common-components/AccessDenied";
import ButtonComponent from "./app/common-components/ButtonComponent";
import ContainerLoader from "./app/common-components/ContainerLoader";
import CustomModal from "./app/common-components/CustomModal";
import NotFound from "./app/common-components/NotFound";
import {
  resetLoggedUser,
  setLoggedUser,
  setPageLoading,
  setSessionExtend,
  setsessionToken,
  setTimeoutId,
} from "./app/commonSlice";
import { authRoutes, routes } from "./app/routes";
import GetProxy from "./app/GetProxy";
import { roleCanViewRoute } from "./app/rbac/permissions";
import {
  isRouteBlockedForUser,
  resolveDeniedPath,
} from "./app/rbac/restrictedUsers";
import { store } from "./app/store";
import { ThemeProvider } from "./app/theme";
import IdleTimercontainer from "./IdleTimercontainer";
import { refreshTokenApi } from "./Modules/Home/HomeApiSlice";
import { resetHomeData } from "./Modules/Home/HomeSlice";
import PrivateRoute from "./PrivateRoute";

// The ToastContainer is mounted above the router so toasts survive navigation,
// which means a live notification card raised moments before logout keeps
// counting down on top of the login page. Logout and the idle timeout both
// navigate to /auth without reloading, so clear anything still on screen as
// soon as we land on an auth route.
const AUTH_PATHS = ["/auth", "/logout"];

const ClearToastsOnAuthRoute = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (AUTH_PATHS.includes(pathname)) {
      toast.dismiss();
    }
  }, [pathname]);
  return null;
};

const SessionTimerIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 12" />
    <line x1="16" y1="16" x2="16" y2="19" />
    <line x1="19" y1="16" x2="19" y2="19" />
  </svg>
);

const formatTimer = (seconds) => {
  const s = Math.max(0, seconds);
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  const mm = String(mins).padStart(2, "0");
  const ss = String(secs).padStart(2, "0");
  return `${mm}:${ss}`;
};

const ModalBody = () => {
  const dispatch = useDispatch();
  const cookies = new Cookies();
  const navigate = useNavigate();
  const proxy = GetProxy(false);
  const TimeoutId = useSelector((state) => state.common.TimeoutId);
  const [timeLeft, setTimeLeft] = useState(60);
  const hasLoggedOut = useRef(false);

  const handleLogout = () => {
    cookies.remove("jwttoken", { path: "/" });
    cookies.remove("Refersh", { path: "/" });
    cookies.remove("apitoken", { path: "/" });
    cookies.remove("username", { path: "/" });
    cookies.remove("referstoken", { path: "/" });
    dispatch(setLoggedUser({ userName: "", userRole: "", token: "" }));
    dispatch(setPageLoading(true));
    navigate("/auth");
  };

  const logout = () => {
    if (TimeoutId) {
      clearTimeout(TimeoutId);
      dispatch(setTimeoutId(null));
    }
    dispatch(setSessionExtend(false));
    sessionStorage.setItem("isLoggedin", false);
    const Apitoken = cookies.get("apitoken");
    const User = cookies.get("username");
    fetch(`${proxy}/auth?logout`, {
      method: "GET",
      headers: {
        "DTOP-API-TOKEN": Apitoken,
        USERNAME: User,
      },
      redirect: "manual",
    })
      .then(() => {
        handleLogout();
        dispatch(resetLoggedUser());
        dispatch(resetHomeData());
        dispatch(setsessionToken(false));
      })
      .catch((err) => {
        console.error(err);
        handleLogout();
      });
  };

  useEffect(() => {
    if (timeLeft <= 0) {
      if (!hasLoggedOut.current) {
        hasLoggedOut.current = true;
        logout();
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleStayOnPage = () => {
    setTimeLeft(60);
    hasLoggedOut.current = false;
    if (TimeoutId) {
      clearTimeout(TimeoutId);
      dispatch(setTimeoutId(null));
    }
    dispatch(setSessionExtend(false));

    dispatch(refreshTokenApi()).then((data) => {
      if (data?.payload?.jwtToken) {
        cookies.set("apitoken", data?.payload?.jwtToken, { path: "/" });
        dispatch(setsessionToken(true));
      }
    });
  };

  return (
    <div className="discard-changes session-timeout-modal">
      <span className="discard-changes__icon">
        <SessionTimerIcon />
      </span>

      <span className="discard-changes__title">
        Your session is about to expire
      </span>

      <span className="discard-changes__message">
        Do you want to stay on the page or log out? You will be logged out
        automatically in
      </span>

      <div className="session-timer-pill">{formatTimer(timeLeft)}</div>

      <div className="discard-changes__actions btnswrap">
        <ButtonComponent
          text="Logout"
          className="cancelBtnStyle btn btn-primary"
          onClick={() => {
            logout();
          }}
        />
        <ButtonComponent
          text="Stay on page"
          className="btnStyle btn btn-primary"
          onClick={() => {
            handleStayOnPage();
          }}
        />
      </div>
    </div>
  );
};

function App() {
  const dispatch = useDispatch();
  const { loggedUser } = useSelector((state) => state.common);
  const { userRole, userName } = loggedUser;
  const CisoPageAccessRole = ["Ciso", "Operation"];
  const isAccessDenied = (routepath) => {
    const rolePaths = {
      "Application Manager": [
        "configuration/administration/directoryConfig",
        "configuration/administration/smtpConfig",
        "configuration/administration/licenseConfig",
        "configuration/administration/userManagement",
        "configuration/administration/loggingConfig",
      ],
    };

    return userRole?.some((role) =>
      rolePaths[role]?.some((path) => routepath.includes(path)),
    );
  };
  const renderRoutes = (allRoutes) => {
    return allRoutes.map((route) =>
      route.childNavs ? (
        renderRoutes(route.childNavs)
      ) : (
        <Route
          key={route.key}
          path={route.path}
          element={
            !userRole ? (
              <Navigate to="/auth" replace />
            ) : userRole.includes("Ciso") &&
              route.path.includes("configuration/administration") ? (
              <Navigate to="/access-denied" replace />
            ) : (userRole.length > 0 &&
                !userRole.some((role) => CisoPageAccessRole.includes(role)) &&
                route.path.includes("gnc/CISOInsights")) ||
              isAccessDenied(route.path) ? (
              <Navigate to="/access-denied" replace />
            ) : !roleCanViewRoute(userRole, route) ||
              isRouteBlockedForUser(userName, route) ? (
              // RBAC: block direct URL access to pages the role can't view, and
              // to pages restricted for this specific account. A role that was
              // only sent to the default landing page goes to its own instead.
              <Navigate
                to={resolveDeniedPath(userName, userRole, route.path)}
                replace
              />
            ) : (
              route.component
            )
          }
        />
      ),
    );
  };
  const sessionExtend = useSelector((state) => state.common.sessionExtend);
  return (
    <Provider store={store}>
      {/* Outside Suspense, so the theme is settled before the first screen —
          including the loader — paints. */}
      <ThemeProvider>
        <ToastContainer position="top-right" />
        <Suspense fallback={<ContainerLoader />}>
          <Router>
            {sessionExtend && (
              <CustomModal
                modalOpen={sessionExtend}
                closeBtn={true}
                closeBtnFuc={() => {
                  dispatch(setSessionExtend(false));
                }}
                modalBody={ModalBody}
              />
            )}
            <IdleTimercontainer />
            <ClearToastsOnAuthRoute />

            <Routes>
              {authRoutes.map((route) => (
                <Route
                  key={route.key}
                  path={route.path}
                  element={route.component}
                />
              ))}

              <Route path="/" element={<PrivateRoute />}>
                {renderRoutes(routes)}
                <Route path="/access-denied" element={<AccessDenied />}></Route>
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </Suspense>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
